import { useState, useEffect, useRef } from 'react';
import { supabase, ScreeningQuestion } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus, Trash2, GripVertical, Edit2, Check, X, ChevronDown, ChevronUp, ToggleLeft
} from 'lucide-react';

const QUESTION_TYPES = [
  { value: 'short_text', label: 'Short text' },
  { value: 'long_text', label: 'Long text' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'multiple_choice', label: 'Multiple choice' },
] as const;

const STARTER_QUESTIONS = [
  { label: 'Do you have the right to work in the USA or Canada?', type: 'yes_no' as const, required: true },
  { label: 'Do you have a valid driver\'s licence?', type: 'yes_no' as const, required: false },
  { label: 'How many years of care experience do you have?', type: 'short_text' as const, required: true },
  { label: 'Are you available for night shifts?', type: 'yes_no' as const, required: false },
  { label: 'When can you start?', type: 'short_text' as const, required: false },
];

type EditingQuestion = {
  id?: string;
  label: string;
  type: ScreeningQuestion['type'];
  options: string[];
  required: boolean;
};

export default function FormBuilder() {
  const { agency } = useAuth();
  const [questions, setQuestions] = useState<ScreeningQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [editForm, setEditForm] = useState<EditingQuestion>({
    label: '', type: 'short_text', options: [], required: false,
  });
  const [newOption, setNewOption] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const dragItem = useRef<string | null>(null);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (agency) loadQuestions();
  }, [agency]);

  async function loadQuestions() {
    setLoading(true);
    const { data } = await supabase
      .from('screening_questions')
      .select('*')
      .eq('agency_id', agency!.id)
      .order('position');
    setQuestions(data ?? []);
    setLoading(false);
  }

  async function seedStarters() {
    if (!agency || seeded) return;
    setSeeded(true);
    const rows = STARTER_QUESTIONS.map((q, i) => ({
      agency_id: agency.id,
      label: q.label,
      type: q.type,
      options: [],
      required: q.required,
      position: questions.length + i,
    }));
    await supabase.from('screening_questions').insert(rows);
    loadQuestions();
  }

  function openNew() {
    setEditingId('new');
    setEditForm({ label: '', type: 'short_text', options: [], required: false });
    setNewOption('');
  }

  function openEdit(q: ScreeningQuestion) {
    setEditingId(q.id);
    setEditForm({ id: q.id, label: q.label, type: q.type, options: q.options ?? [], required: q.required });
    setNewOption('');
  }

  function cancelEdit() {
    setEditingId(null);
    setNewOption('');
  }

  async function saveQuestion() {
    if (!agency || !editForm.label.trim()) return;
    setSaving(true);

    if (editingId === 'new') {
      await supabase.from('screening_questions').insert({
        agency_id: agency.id,
        label: editForm.label.trim(),
        type: editForm.type,
        options: editForm.options,
        required: editForm.required,
        position: questions.length,
      });
    } else {
      await supabase.from('screening_questions').update({
        label: editForm.label.trim(),
        type: editForm.type,
        options: editForm.options,
        required: editForm.required,
      }).eq('id', editingId!);
    }

    setSaving(false);
    setEditingId(null);
    loadQuestions();
  }

  async function deleteQuestion(id: string) {
    await supabase.from('screening_questions').delete().eq('id', id);
    setQuestions((q) => q.filter((x) => x.id !== id));
  }

  async function toggleRequired(q: ScreeningQuestion) {
    await supabase.from('screening_questions').update({ required: !q.required }).eq('id', q.id);
    setQuestions((qs) => qs.map((x) => x.id === q.id ? { ...x, required: !x.required } : x));
  }

  function handleDragStart(id: string) { dragItem.current = id; }

  function handleDragOver(e: React.DragEvent, id: string) {
    e.preventDefault();
    setDragOver(id);
  }

  async function handleDrop(targetId: string) {
    const fromId = dragItem.current;
    if (!fromId || fromId === targetId) { setDragOver(null); dragItem.current = null; return; }

    const reordered = [...questions];
    const fromIdx = reordered.findIndex((q) => q.id === fromId);
    const toIdx = reordered.findIndex((q) => q.id === targetId);
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);

    const updated = reordered.map((q, i) => ({ ...q, position: i }));
    setQuestions(updated);
    setDragOver(null);
    dragItem.current = null;

    await Promise.all(
      updated.map((q) => supabase.from('screening_questions').update({ position: q.position }).eq('id', q.id))
    );
  }

  function addOption() {
    if (newOption.trim()) {
      setEditForm((f) => ({ ...f, options: [...f.options, newOption.trim()] }));
      setNewOption('');
    }
  }

  function removeOption(i: number) {
    setEditForm((f) => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }));
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Screening Questions</h2>
          <p className="text-sm text-gray-500 mt-0.5">Customise your application form. Drag to reorder.</p>
        </div>
        <div className="flex gap-2">
          {questions.length === 0 && (
            <button
              onClick={seedStarters}
              className="px-3 py-2 text-sm text-blue-600 border border-blue-200 hover:bg-blue-50 rounded-md font-medium transition-colors"
            >
              Use starter questions
            </button>
          )}
          <button
            onClick={openNew}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add question
          </button>
        </div>
      </div>

      {/* New question form */}
      {editingId === 'new' && (
        <EditForm
          form={editForm}
          setForm={setEditForm}
          newOption={newOption}
          setNewOption={setNewOption}
          addOption={addOption}
          removeOption={removeOption}
          onSave={saveQuestion}
          onCancel={cancelEdit}
          saving={saving}
        />
      )}

      {questions.length === 0 && editingId !== 'new' ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <div className="text-gray-400 text-4xl mb-3">?</div>
          <p className="text-gray-500 font-medium">No questions yet</p>
          <p className="text-gray-400 text-sm mt-1">Add your first question or use our starter set.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {questions.map((q, i) => (
            <div key={q.id}>
              {editingId === q.id ? (
                <EditForm
                  form={editForm}
                  setForm={setEditForm}
                  newOption={newOption}
                  setNewOption={setNewOption}
                  addOption={addOption}
                  removeOption={removeOption}
                  onSave={saveQuestion}
                  onCancel={cancelEdit}
                  saving={saving}
                />
              ) : (
                <div
                  draggable
                  onDragStart={() => handleDragStart(q.id)}
                  onDragOver={(e) => handleDragOver(e, q.id)}
                  onDrop={() => handleDrop(q.id)}
                  onDragLeave={() => setDragOver(null)}
                  className={`flex items-start gap-3 p-4 bg-white border rounded-lg transition-colors ${
                    dragOver === q.id ? 'border-blue-400 shadow-lg shadow-blue-100' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="cursor-grab mt-0.5 text-gray-300 hover:text-gray-500">
                    <GripVertical className="w-4 h-4" />
                  </div>
                  <span className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{q.label}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                        {QUESTION_TYPES.find((t) => t.value === q.type)?.label}
                      </span>
                      {q.required && (
                        <span className="text-xs px-2 py-0.5 bg-red-50 text-red-500 rounded-full font-medium">Required</span>
                      )}
                      {q.type === 'multiple_choice' && q.options?.length > 0 && (
                        <span className="text-xs text-gray-400">{q.options.length} options</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => toggleRequired(q)}
                      title={q.required ? 'Mark optional' : 'Mark required'}
                      className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                        q.required ? 'bg-red-50 text-red-500 hover:bg-red-100' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                      }`}
                    >
                      <ToggleLeft className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => openEdit(q)}
                      className="w-7 h-7 rounded-lg bg-gray-50 text-gray-400 hover:bg-blue-50 hover:text-blue-600 flex items-center justify-center transition-colors"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => deleteQuestion(q.id)}
                      className="w-7 h-7 rounded-lg bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditForm({
  form, setForm, newOption, setNewOption, addOption, removeOption, onSave, onCancel, saving,
}: {
  form: EditingQuestion;
  setForm: React.Dispatch<React.SetStateAction<EditingQuestion>>;
  newOption: string;
  setNewOption: (v: string) => void;
  addOption: () => void;
  removeOption: (i: number) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  return (
    <div className="p-5 bg-blue-50 border border-blue-200 rounded-lg mb-3 space-y-4">
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Question</label>
        <input
          type="text"
          value={form.label}
          onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
          placeholder="Enter your question..."
          className="w-full px-3 py-2.5 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Type</label>
          <select
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as ScreeningQuestion['type'] }))}
            className="w-full px-3 py-2.5 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            {QUESTION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col justify-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => setForm((f) => ({ ...f, required: !f.required }))}
              className={`w-10 h-5 rounded-full transition-colors duration-200 relative ${form.required ? 'bg-blue-600' : 'bg-gray-200'}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${form.required ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-sm text-gray-700 font-medium">Required</span>
          </label>
        </div>
      </div>

      {form.type === 'multiple_choice' && (
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Options</label>
          <div className="space-y-2 mb-2">
            {form.options.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="flex-1 text-sm px-3 py-1.5 bg-white rounded-lg border border-gray-200">{opt}</span>
                <button onClick={() => removeOption(i)} className="text-gray-400 hover:text-red-500">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
              placeholder="Add option..."
              className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
            <button
              onClick={addOption}
              className="px-3 py-2 bg-white border border-gray-200 rounded-md text-gray-600 hover:bg-blue-50 hover:border-blue-200 text-sm transition-colors"
            >
              Add
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button
          onClick={onSave}
          disabled={saving || !form.label.trim()}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-md transition-colors"
        >
          <Check className="w-3.5 h-3.5" />
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}
